import { useTranslation } from '@shared/core';
import globalWindow from '@shared/core/globals';
import { differenceInDays, intervalToDuration, isPast } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { useRef, useState, useEffect } from 'react';

interface CountdownState
  extends Readonly<{
    days?: number;
    hrs?: number;
    mins?: number;
  }> {}

const CountdownDefaultState: CountdownState = {
  days: 0,
  hrs: 0,
  mins: 0,
} as const;

/**
 * create event count down object
 * if event in the past then time will increase
 * if event in the future then time will decrease
 */
function _createTimerDuration(startDate: Date | number, timeZone: string): CountdownState {
  const now = Date.now();

  const duration =
    intervalToDuration({
      start: startDate,
      end: zonedTimeToUtc(now, timeZone),
    }) || CountdownDefaultState;

  const days = Math.abs(differenceInDays(startDate, now));
  return {
    days,
    hrs: duration.hours,
    mins: duration.minutes,
  };
}

export const useCountdown = (milliseconds: number = 0, timezone: string = 'America/Los_Angeles') => {
  const hasPassedRef = useRef<boolean>(isPast(milliseconds));
  const [countDown, setCountdown] = useState<CountdownState>(_createTimerDuration(milliseconds, timezone) || CountdownDefaultState);
  const { t } = useTranslation('joykit');
  const eventCountDownTranslations = t('eventCountdown');

  useEffect(() => {
    const interval = globalWindow.setInterval(() => {
      hasPassedRef.current = isPast(milliseconds);
      setCountdown(_createTimerDuration(milliseconds, timezone));
    }, 1000);
    return function cleanup() {
      globalWindow.clearInterval(interval);
    };
  }, [milliseconds, timezone]);

  const durationToDisplay = hasPassedRef.current ? eventCountDownTranslations.displayTextAgo(countDown) : eventCountDownTranslations.displayText(countDown);
  return {
    formattedDuration: durationToDisplay,
    countDown,
  };
};
